import { Box, Flex, Text, useColorModeValue, useToken } from '@chakra-ui/react';
import { useMemo, useState, useCallback } from 'react';
import {
  InlineBarChart,
  InlineBarPopover,
} from '@investigator/shared/components/InlineBarChart';
import { i18n } from '@investigator/shared/localization';
import {
  Detection,
  DetectionGroups,
} from '@investigator/shared/generated/graphql';
import { useGetTopDetectionsByDetectionGroupsPerEntity } from '../../../detections/queries';
import { SEVERITY_COLOR_TOKENS } from './severityColors';
import { SeverityBucket } from '@investigator/shared/types';

const DETECTION_COUNT_IN_BUCKET_SIZE = 5;

export interface SeverityLevelCounts {
  low: number | null;
  medium: number | null;
  high: number | null;
}

interface SeverityBreakdownBarChartProps {
  severityLevelCounts: SeverityLevelCounts | null;
  tenant_entity: string;
  tenant: string;
  containerWidthPx?: number;
  showTitle?: boolean;
  titleFormat?: 'count-first' | 'label-first';
  selectedEntityStart?: number;
  selectedEntityEnd?: number;
}

const { totalDetections: totalDetectionsText, totalDetectionsNumber } =
  i18n.pages.entities.severityBreakdownBarChart;

export const SeverityBreakdownBarChart = ({
  severityLevelCounts,
  tenant_entity,
  tenant,
  containerWidthPx,
  showTitle = true,
  titleFormat = 'count-first',
  selectedEntityStart,
  selectedEntityEnd,
}: SeverityBreakdownBarChartProps) => {
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const { data, loading } = useGetTopDetectionsByDetectionGroupsPerEntity({
    variables: {
      start: selectedEntityStart ?? 0,
      end: selectedEntityEnd ?? 0,
      tenant_entity,
      tenant,
      group_by: DetectionGroups.SeverityLevels,
      size: DETECTION_COUNT_IN_BUCKET_SIZE,
    },
    skip:
      !hasFetchedData ||
      !selectedEntityStart ||
      !selectedEntityEnd ||
      !tenant_entity ||
      !tenant,
  });

  const handleFetchData = useCallback((isOpen: boolean) => {
    if (isOpen) {
      setHasFetchedData(true);
    }
  }, []);

  const counts = useMemo(
    () => ({
      low: severityLevelCounts?.low ?? 0,
      medium: severityLevelCounts?.medium ?? 0,
      high: severityLevelCounts?.high ?? 0,
    }),
    [
      severityLevelCounts?.low,
      severityLevelCounts?.medium,
      severityLevelCounts?.high,
    ],
  );

  const [
    highColorLight,
    highColorDark,
    mediumColorLight,
    mediumColorDark,
    lowColorLight,
    lowColorDark,
  ] = useToken('colors', [
    SEVERITY_COLOR_TOKENS.high.light,
    SEVERITY_COLOR_TOKENS.high.dark,
    SEVERITY_COLOR_TOKENS.medium.light,
    SEVERITY_COLOR_TOKENS.medium.dark,
    SEVERITY_COLOR_TOKENS.low.light,
    SEVERITY_COLOR_TOKENS.low.dark,
  ]);

  const highColor = useColorModeValue(highColorLight, highColorDark);
  const mediumColor = useColorModeValue(mediumColorLight, mediumColorDark);
  const lowColor = useColorModeValue(lowColorLight, lowColorDark);

  const totalDetections = useMemo(() => {
    return counts.low + counts.medium + counts.high;
  }, [counts]);

  const barData = useMemo(() => {
    const data: Array<{
      color: string;
      number: number;
      bucketLevel: SeverityBucket;
    }> = [];

    if (counts.low > 0) {
      data.push({
        color: lowColor,
        number: counts.low,
        bucketLevel: SeverityBucket.Low,
      });
    }

    if (counts.medium > 0) {
      data.push({
        color: mediumColor,
        number: counts.medium,
        bucketLevel: SeverityBucket.Medium,
      });
    }

    if (counts.high > 0) {
      data.push({
        color: highColor,
        number: counts.high,
        bucketLevel: SeverityBucket.High,
      });
    }

    return data;
  }, [counts, highColor, mediumColor, lowColor]);

  const getDetectionsByBucket = useMemo(
    () =>
      (bucket: SeverityBucket | undefined): Detection[] => {
        const detectionsBySeverityBucket =
          data?.getTopDetectionsByDetectionGroupsPerEntity?.data ?? [];

        if (!detectionsBySeverityBucket) return [];

        const bucketData = detectionsBySeverityBucket?.find(
          (item) => item?.group_key === bucket,
        );

        return (bucketData?.detections || []).filter(
          (detection): detection is Detection => detection != null,
        );
      },
    [data],
  );

  const titleText =
    titleFormat === 'label-first' ? (
      <Flex justifyContent="space-between">
        <Text>{totalDetectionsText} </Text>
        <Text>{totalDetections}</Text>
      </Flex>
    ) : (
      i18n.formatString(totalDetectionsNumber, totalDetections).toString()
    );

  return (
    <Box data-testid="severity-breakdown-bar-chart" overflow="visible">
      {showTitle && (
        <Box
          marginY={1}
          data-testid="severity-breakdown-total-text"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {titleText}
        </Box>
      )}
      <Box maxWidth={containerWidthPx ? `${containerWidthPx}px` : '100%'}>
        <InlineBarChart
          barData={barData}
          total={totalDetections}
          containerWidthPx={containerWidthPx}
          renderSegment={({ barData: segment, width, index }) => (
            <InlineBarPopover
              key={`${segment.color}-${index}`}
              color={segment.color}
              width={width}
              bucketLevel={segment.bucketLevel}
              totalDetections={segment.number}
              detections={getDetectionsByBucket(segment.bucketLevel)}
              onFetchData={handleFetchData}
              loading={loading}
              selectedEntityStart={selectedEntityStart}
              selectedEntityEnd={selectedEntityEnd}
            />
          )}
        />
      </Box>
    </Box>
  );
};
